import { msg } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";
import { ChevronDown } from "lucide-react";
import { useId, useState } from "react";

import { cn } from "@/shared/lib";
import { Button } from "@/shared/ui/button";
import { Input } from "@/shared/ui/input";
import { Popover, PopoverClose, PopoverContent, PopoverTrigger } from "@/shared/ui/popover";

import { useAssets } from "../../api/useAssets";
import { type HeroView, useHeroLookup } from "../../api/useHeroLookup";
import type { HeroStat } from "../../lib/heroStats";
import { HeroAvatar } from "../HeroAvatar";
import { filterTriggerClassName } from "./filterTriggerClassName";
import { HeroFilterTile } from "./HeroFilterTile";

// Сколько выбранных героев видно иконками на кнопке.
const STACK_SIZE = 3;

const HEROES_COUNT = msg({
  message: "{count, plural, one {# hero} other {# heroes}}",
});

type HeroFilterProps = {
  selected: readonly number[];
  // Герои загруженных матчей: самые частые сверху.
  played: readonly HeroStat[];
  onChange: (heroes: number[]) => void;
};

// Выбор нескольких героев: поиск по названию на любом языке, у каждого — сколько матчей.
// Сначала герои загруженных матчей, ниже серым — остальные герои, которых можно выбрать
// в игре. Отметка сразу меняет список, «Готово» только закрывает выбор.
export const HeroFilter = ({ selected, played, onChange }: HeroFilterProps) => {
  const { i18n, t } = useLingui();
  const lookup = useHeroLookup();
  const [query, setQuery] = useState("");

  const assets = useAssets();
  const headingId = useId();
  const needle = query.trim().toLocaleLowerCase();
  const matchesQuery = (hero: HeroView) =>
    needle === "" || hero.aliases.some((alias) => alias.toLocaleLowerCase().includes(needle));
  const playedIds = new Set(played.map((stat) => stat.heroId));
  const sections = [
    {
      id: "played",
      title: <Trans>You played</Trans>,
      tiles: played.map((stat) => ({ hero: lookup(stat.heroId), games: stat.games })),
    },
    {
      id: "others",
      title: <Trans>Other heroes</Trans>,
      tiles: (assets.data?.heroes ?? [])
        .filter((hero) => hero.playable && !playedIds.has(hero.id))
        .map((hero) => ({ hero: lookup(hero.id), games: 0 }))
        .sort((a, b) => a.hero.name.localeCompare(b.hero.name, i18n.locale)),
    },
  ]
    .map((section) => ({
      ...section,
      tiles: section.tiles.filter(({ hero }) => matchesQuery(hero)),
    }))
    .filter((section) => section.tiles.length > 0);
  const selectedHeroes = selected.map(lookup);
  const [firstSelected] = selectedHeroes;
  const selectedCount = selected.length;
  const toggle = (heroId: number) => {
    onChange(
      selected.includes(heroId) ? selected.filter((id) => id !== heroId) : [...selected, heroId],
    );
  };

  return (
    <Popover
      onOpenChange={(open) => {
        if (!open) {
          setQuery("");
        }
      }}
    >
      <PopoverTrigger asChild>
        <Button variant="outline" className={filterTriggerClassName(selected.length > 0)}>
          {firstSelected === undefined ? (
            <Trans>All heroes</Trans>
          ) : (
            <>
              <span className="flex">
                {selectedHeroes.slice(0, STACK_SIZE).map((hero, index) => (
                  <HeroAvatar
                    key={hero.id}
                    name={hero.name}
                    iconUrl={hero.iconUrl}
                    size={26}
                    className={cn(
                      "rounded-full border-2 border-surface text-xs",
                      index > 0 && "-ml-2",
                    )}
                  />
                ))}
              </span>
              <span>
                {selectedHeroes.length === 1
                  ? firstSelected.name
                  : i18n._({ ...HEROES_COUNT, values: { count: selectedHeroes.length } })}
              </span>
            </>
          )}
          <ChevronDown aria-hidden="true" className="text-subtle" />
        </Button>
      </PopoverTrigger>
      <PopoverContent aria-label={t`Choose heroes`} className="flex w-[680px] flex-col gap-3">
        <Input
          type="search"
          value={query}
          onChange={(event) => {
            setQuery(event.target.value);
          }}
          placeholder={t`Find a hero`}
          aria-label={t`Find a hero`}
        />
        <div className="flex min-h-8 items-center justify-between gap-2.5 text-[13px]">
          <span className="text-muted">
            {selected.length === 0 ? (
              <Trans>You can pick several</Trans>
            ) : (
              t`Selected: ${selectedCount}`
            )}
          </span>
          {selected.length > 0 && (
            <Button
              variant="link"
              size="sm"
              onClick={() => {
                onChange([]);
              }}
            >
              <Trans>Clear all</Trans>
            </Button>
          )}
        </div>
        <div className="flex max-h-[360px] flex-col gap-3 overflow-y-auto pr-1">
          {sections.length === 0 ? (
            <p className="p-5 text-center text-subtle">
              <Trans>No one found</Trans>
            </p>
          ) : (
            sections.map((section) => (
              <section
                key={section.id}
                aria-labelledby={`${headingId}-${section.id}`}
                className="flex flex-col gap-2"
              >
                <h3
                  id={`${headingId}-${section.id}`}
                  className="text-xs tracking-[0.06em] text-subtle uppercase"
                >
                  {section.title}
                </h3>
                <ul className="grid grid-cols-[repeat(auto-fill,minmax(76px,1fr))] gap-2">
                  {section.tiles.map(({ hero, games }) => (
                    <li key={hero.id}>
                      <HeroFilterTile
                        hero={hero}
                        games={games}
                        isSelected={selected.includes(hero.id)}
                        onToggle={toggle}
                      />
                    </li>
                  ))}
                </ul>
              </section>
            ))
          )}
        </div>
        <PopoverClose asChild>
          <Button className="h-10 self-end px-4.5 font-semibold">
            <Trans>Done</Trans>
          </Button>
        </PopoverClose>
      </PopoverContent>
    </Popover>
  );
};
